/**
 * LandingES.js — /es
 * Dedicated Spanish-language landing page for 1099 contractors & freelancers.
 * Design: Navy/Teal/Green system matching AuditLanding v2.
 * CTA: drives to /quiz (same funnel as English audit page).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY  = "#10172a";   // Ebony — matches new app palette
const TEAL  = "#08976c";   // Gossamer — primary action color
const GREEN = "#07845f";   // Gossamer dark — hover/CTA
const LGRAY = "#f5f5f4";
const DARK  = "#1c1917";
const GRAY  = "#57534e";

/* ── Helpers ─────────────────────────────────────────────────────────────── */
const fmt = n => n != null ? `$${Number(n).toLocaleString('en-US')}` : '—';

/* ── Sub-components ──────────────────────────────────────────────────────── */
const H2Bar = ({ children, barColor = NAVY }) => (
  <div style={{ background: barColor, padding: "18px 24px", marginBottom: 36 }}>
    <h2 style={{
      maxWidth: 880, margin: "0 auto",
      fontSize: "clamp(20px,3.2vw,28px)", fontWeight: 800,
      color: "#fff", letterSpacing: "-0.01em", lineHeight: 1.25,
    }}>
      {children}
    </h2>
  </div>
);

const Sec = ({ children, bg = "#fff" }) => (
  <section style={{ background: bg, paddingBottom: 72 }}>
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
      {children}
    </div>
  </section>
);

const Btn = ({ children, onClick, fullWidth = false, size = "lg" }) => {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      data-testid="es-cta-btn"
      style={{
        background: hov ? "#065f43" : GREEN,
        color: "#fff", border: "none", borderRadius: 8,
        padding: size === "lg" ? "18px 36px" : "14px 28px",
        fontSize: size === "lg" ? 17 : 15, fontWeight: 700,
        cursor: "pointer", width: fullWidth ? "100%" : "auto",
        maxWidth: fullWidth ? 420 : "none", transition: "background 0.15s",
        lineHeight: 1.3,
      }}
    >
      {children}
    </button>
  );
};

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #e7e5e4", padding: "18px 0" }}>
      <button onClick={() => setOpen(!open)} style={{
        background: "none", border: "none", textAlign: "left",
        cursor: "pointer", fontWeight: 600, fontSize: 16,
        color: DARK, width: "100%",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        lineHeight: 1.4, padding: 0, minHeight: 48,
      }}>
        {q}
        <span style={{ fontSize: 22, color: TEAL, flexShrink: 0, marginLeft: 12 }}>
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <p style={{ marginTop: 12, color: GRAY, lineHeight: 1.85, fontSize: 15 }}>{a}</p>
      )}
    </div>
  );
};

/* ── Main page ────────────────────────────────────────────────────────────── */
export default function LandingES() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({
    price_display: "$17", spots_remaining: 53, is_launch: true, last_claimed_at: null,
  });

  useEffect(() => {
    axios.get(`${API}/quiz/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const goToQuiz = () => {
    // Set Spanish as the active language before entering the quiz
    localStorage.setItem('solelytax-language', 'es');
    navigate("/quiz");
  };
  const spotsLeft = pricing.spots_remaining ?? 53;

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflowX: "hidden", color: DARK,
    }}>
      <style>{`
        @media (max-width: 640px) {
          .es-qual-grid   { grid-template-columns: 1fr !important; }
          .es-steps-grid  { grid-template-columns: 1fr !important; }
          .es-isnt-grid   { grid-template-columns: 1fr !important; }
          .es-testi-grid  { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{
        background: NAVY, padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #1a3d6b",
      }}>
        <span style={{ fontWeight: 800, fontSize: 18, color: "#4ed3a6" }}>SolelyTax AI</span>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <a href="/audit" style={{ fontSize: 13, color: "#94a3b8", textDecoration: "none" }}>
            English
          </a>
          <button onClick={goToQuiz} data-testid="es-nav-btn" style={{
            background: GREEN, color: "#fff", border: "none", borderRadius: 6,
            padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 14,
          }}>
            Empezar gratis →
          </button>
        </div>
      </nav>

      {/* ══ SECCIÓN 1 — HERO (Navy) ════════════════════════════════════════ */}
      <section style={{
        background: NAVY, padding: "80px 24px 64px", textAlign: "center",
        minHeight: "80vh", display: "flex", alignItems: "center",
      }}>
        <div style={{ maxWidth: 760, margin: "0 auto", width: "100%" }}>
          <div style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#6ee7b7", marginBottom: 22,
          }}>
            Para contratistas 1099, freelancers y emprendedores que ganan $100K–$2M+
          </div>

          <h1 style={{
            fontSize: "clamp(30px,5.5vw,52px)", fontWeight: 900, lineHeight: 1.1,
            marginBottom: 28, color: "#f1f5f9", letterSpacing: "-0.025em",
          }}>
            El cuestionario de 90 segundos que revela exactamente cuánto estás pagando
            de más en impuestos — y cómo recuperarlo.
          </h1>

          <p style={{
            fontSize: "clamp(16px,2.2vw,19px)", color: "#94a3b8",
            marginBottom: 40, lineHeight: 1.75, maxWidth: 640, margin: "0 auto 40px",
          }}>
            Responde 12 preguntas. Recibe un reporte personalizado de 15–20 páginas con
            cada estrategia fiscal aplicable a tu situación — ordenadas por impacto económico.
          </p>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            <Btn onClick={goToQuiz} fullWidth size="lg">
              → Comenzar mi auditoría gratuita de 90 segundos
            </Btn>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", lineHeight: 1.7 }}>
              <span style={{ textDecoration: "line-through", color: "#64748b", marginRight: 4 }}>$27</span>
              <span style={{ color: "#f1f5f9", fontWeight: 700 }}> $17 hoy</span>
              {" · "}{spotsLeft} de 200 lugares disponibles{" · "}Garantía de 30 días{" · "}Entrega inmediata
            </div>
          </div>

          <div style={{ marginTop: 28, color: "#94a3b8", fontSize: 14, fontWeight: 500 }}>
            <span style={{ color: "#fbbf24" }}>★★★★★</span>
            {" "}Más de 300 profesionales independientes ya optimizaron sus impuestos
          </div>
        </div>
      </section>

      {/* ── BARRA DE URGENCIA (Teal) ─────────────────────────────────────── */}
      <div style={{
        background: TEAL, padding: "14px 24px", textAlign: "center",
        fontSize: 15, fontWeight: 700, color: "#fff",
      }}>
        ⚡ Precio de lanzamiento: <span data-testid="es-spots-remaining">{spotsLeft}</span> de 200 lugares disponibles a $17.{" "}
        <span style={{ fontWeight: 400 }}>Después de 200: sube a $27. Sin excepciones.</span>
      </div>

      {/* ══ SECCIÓN 2 — ¿ERES TÚ? (White | Navy H2) ══════════════════════ */}
      <Sec bg="#fff">
        <H2Bar barColor={NAVY}>¿Esto es para ti?</H2Bar>

        <div className="es-qual-grid" style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24,
        }}>
          <div style={{
            background: "#f0fdf4", border: "1px solid #a7f3d0",
            borderTop: `4px solid ${GREEN}`, padding: 24, borderRadius: 4,
          }}>
            <div style={{ fontWeight: 800, color: "#065f46", marginBottom: 16, fontSize: 15 }}>
              ✅ Este reporte es para ti si...
            </div>
            {[
              "Generas $100K–$2M+ como contratista 1099, freelancer o dueño de negocio",
              "Tienes un contador, pero solo hablan en temporada de impuestos",
              "Has escuchado sobre S-Corps, SEP IRA, Regla Augusta — pero no sabes si aplican",
              "Sospechas que estás pagando de más pero no puedes cuantificarlo",
              "Quieres una agenda concreta para tu próxima reunión con tu CPA",
            ].map((t, i) => (
              <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                ✓ {t}
              </p>
            ))}
          </div>

          <div style={{
            background: "#fff1f2", border: "1px solid #fecaca",
            borderTop: "4px solid #dc2626", padding: 24, borderRadius: 4,
          }}>
            <div style={{ fontWeight: 800, color: "#7f1d1d", marginBottom: 16, fontSize: 15 }}>
              ❌ Este reporte NO es para ti si...
            </div>
            {[
              "Ganas menos de $80K sin ingresos de negocio",
              "Necesitas que alguien prepare tu declaración — esto es planificación, no filing",
              "Ya tienes sesiones trimestrales de estrategia con tu CPA",
              "Buscas estrategias agresivas o de zona gris",
            ].map((t, i) => (
              <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                ✗ {t}
              </p>
            ))}
          </div>
        </div>

        <div style={{
          background: "#f0fdf4", border: `1px solid ${TEAL}`,
          borderLeft: `4px solid ${TEAL}`, padding: "16px 22px",
          color: "#065f46", fontSize: 15, fontWeight: 600, lineHeight: 1.6,
        }}>
          ✅ Si marcaste 3 o más casillas de la izquierda — este reporte casi seguro
          revelará algo que tu configuración actual está dejando en la mesa.
        </div>
      </Sec>

      {/* ══ SECCIÓN 3 — PROBLEMA / AGITACIÓN (Gray | Teal H2) ════════════ */}
      <Sec bg={LGRAY}>
        <H2Bar barColor={TEAL}>El problema no es tu contador. Es que llegas sin un plan.</H2Bar>

        <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
          Tu contador está entrenado para presentar tu declaración con precisión.
        </p>
        <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
          No está entrenado — ni tiene incentivos — para auditar toda tu estructura de ingresos,
          identificar proactivamente estrategias que no le hayas preguntado, o explicarte todo
          lo que te estás perdiendo. Así funciona esa relación.
        </p>
        <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
          Así que sales de cada reunión igual que entraste: sospechando que pagas de más,
          sin poder probarlo, sin saber qué hacer diferente el año que viene.
        </p>
        <p style={{ fontSize: 17, fontWeight: 800, color: DARK, marginBottom: 28 }}>
          No preguntas. Los ahorros se quedan en la mesa. Cada año.
        </p>

        <div style={{
          background: "#fff", borderLeft: `4px solid ${NAVY}`,
          padding: "20px 24px", fontStyle: "italic", color: "#374151",
          fontSize: 16, lineHeight: 1.8, fontWeight: 500, marginBottom: 28,
        }}>
          "El profesional independiente promedio con altos ingresos deja entre $20,000 y $80,000
          en la mesa cada año. No porque esté haciendo algo mal — sino porque nunca tuvo un
          sistema que le dijera qué preguntar."
        </div>

        <div style={{ textAlign: "center" }}>
          <Btn onClick={goToQuiz} size="lg">
            → Obtener mi reporte personalizado — $17
          </Btn>
          <div style={{ marginTop: 10, fontSize: 13, color: "#78716c" }}>
            Garantía de 30 días · Entrega inmediata · Precio de lanzamiento
          </div>
        </div>
      </Sec>

      {/* ══ SECCIÓN 4 — TESTIMONIOS (Navy | Teal H2) ══════════════════════ */}
      <Sec bg={NAVY}>
        <H2Bar barColor={TEAL}>Lo que pasa cuando llegas preparado</H2Bar>

        <div className="es-testi-grid" style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24,
        }}>
          {[
            [
              "Tengo contador hace seis años. Este reporte identificó tres estrategias que nunca habíamos discutido. Llegué a nuestra siguiente reunión con el reporte en mano y fue la conversación más productiva que hemos tenido.",
              "Dueño de agencia", "$380K de ingresos anuales",
            ],
            [
              "Sabía que existía el S-Corp. Mi contador lo mencionó una vez y dijo que 'lo veríamos'. Pasaron dos años. El reporte me dio la pregunta exacta que hacer. Presentamos la elección tres semanas después.",
              "Consultor independiente", "$220K de ingresos anuales",
            ],
            [
              "Mi puntuación fue 41 de 100. Creía que estaba bien. El rango estimado de pago en exceso — $35,000 a $72,000 — me enojó. Luego usé la agenda de reunión y realmente hice algo al respecto.",
              "Profesional 1099", "$310K de ingresos anuales",
            ],
          ].map(([quote, role, income], i) => (
            <div key={i} style={{
              background: "#1a3d6b", border: "1px solid #2a5280",
              borderRadius: 4, padding: 24,
            }}>
              <div style={{ color: "#fbbf24", fontSize: 14, marginBottom: 12 }}>★★★★★</div>
              <p style={{ fontStyle: "italic", color: "#cbd5e1", fontSize: 14, lineHeight: 1.8, marginBottom: 14 }}>
                "{quote}"
              </p>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#4ed3a6" }}>
                — {role} · {income}
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                Resultados individuales pueden variar. No es asesoría fiscal.
              </div>
            </div>
          ))}
        </div>
      </Sec>

      {/* ══ SECCIÓN 5 — QUÉ INCLUYE (White | Navy H2) ════════════════════ */}
      <Sec bg="#fff">
        <H2Bar barColor={NAVY}>Tu reporte personalizado de estrategia fiscal (15–20 páginas)</H2Bar>

        <p style={{ color: TEAL, fontWeight: 600, fontSize: 16, marginBottom: 32 }}>
          Basado en tus respuestas. Entregado en minutos. Diseñado para llevar a tu próxima
          reunión con tu CPA.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {[
            ["🎯", "Tu Puntuación de Preparación con el CPA (0–100)",
             "Un número que muestra qué tan optimizada está tu configuración actual — con explicación en español sencillo de qué está creando la brecha y cuánto te está costando."],
            ["💰", "Rango estimado de pago en exceso",
             "Un rango en dólares calculado a partir de tu nivel de ingresos, estructura y las estrategias que no estás usando. La mayoría se sorprende de este número."],
            ["📊", "Análisis de brechas en tu estrategia fiscal",
             "Cada estrategia aplicable a tu perfil — ordenada por impacto en dólares — con tu estado actual (activa / brecha / desconocida) y ahorro anual estimado."],
            ["📋", "Fichas de estrategia (una por brecha identificada)",
             "Para cada brecha: cómo funciona, cuánto vale a tu nivel de ingresos, qué puede salir mal y las preguntas específicas para llevar a tu CPA."],
            ["📄", "Tu agenda de reunión con el CPA",
             "Un documento de una página que entregas directamente a tu contador. Temas específicos. Preguntas específicas. La diferencia entre '¿me estoy perdiendo algo?' y una conversación real de optimización."],
            ["⚡", "Tu plan de victorias rápidas en 7 días",
             "Los movimientos más rápidos que puedes hacer esta semana — ordenados por impacto, con estimados de tiempo específicos."],
          ].map(([icon, title, desc], i) => (
            <div key={i} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{
                width: 44, height: 44, background: NAVY, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, flexShrink: 0,
              }}>
                {icon}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: DARK, marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 14, color: GRAY, lineHeight: 1.75 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Sec>

      {/* ══ SECCIÓN 6 — CÓMO FUNCIONA (Gray | Teal H2) ═══════════════════ */}
      <Sec bg={LGRAY}>
        <H2Bar barColor={TEAL}>3 pasos. 90 segundos. Un reporte construido para tu situación.</H2Bar>

        <div className="es-steps-grid" style={{
          display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24,
        }}>
          {[
            ["01", "Responde 12 preguntas", "~90 segundos",
             "Nivel de ingresos, estructura de negocio, estrategias actuales, relación con tu CPA. Sin datos sensibles. Sin acceso a cuentas."],
            ["02", "Recibe tu reporte de inmediato", "En minutos",
             "Tu reporte personalizado de 15–20 páginas se genera a partir de tus respuestas y se entrega como PDF a tu correo en minutos."],
            ["03", "Llega preparado", "Reunión con CPA",
             "Usa las fichas de estrategia y la agenda para tener una conversación que nunca habías podido tener antes. Imprime la página 16. Entrégasela a tu CPA."],
          ].map(([num, title, time, desc], i) => (
            <div key={i} style={{
              background: "#fff", borderTop: `4px solid ${NAVY}`,
              padding: 28, textAlign: "center",
            }}>
              <div style={{
                fontSize: 36, fontWeight: 900, color: NAVY,
                marginBottom: 8, lineHeight: 1,
              }}>{num}</div>
              <div style={{ fontWeight: 800, fontSize: 17, color: DARK, marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, color: TEAL, marginBottom: 12, fontWeight: 700 }}>{time}</div>
              <div style={{ fontSize: 14, color: GRAY, lineHeight: 1.7 }}>{desc}</div>
            </div>
          ))}
        </div>
      </Sec>

      {/* ══ SECCIÓN 7 — ES / NO ES (White | Navy H2) ══════════════════════ */}
      <Sec bg="#fff">
        <H2Bar barColor={NAVY}>Construido sobre una premisa: mereces información específica, no consejos genéricos.</H2Bar>

        <div className="es-isnt-grid" style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24,
        }}>
          <div style={{
            background: "#f0fdf4", border: "1px solid #a7f3d0",
            borderTop: `4px solid ${GREEN}`, padding: 24, borderRadius: 4,
          }}>
            <div style={{ fontWeight: 800, color: "#065f46", marginBottom: 16, fontSize: 15 }}>
              ✅ Este reporte SÍ es
            </div>
            {[
              "Un informe de planificación personalizado basado en tus respuestas específicas",
              "Un análisis de brechas que muestra qué estrategias legales aplican a ti",
              "Una herramienta que hace que tu CPA actual sea mucho más útil",
              "Un PDF que eres dueño, referencias y puedes llevar a cualquier asesor",
              "Cada estrategia tiene un número de sección en el código fiscal",
            ].map((t, i) => (
              <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                ✓ {t}
              </p>
            ))}
          </div>

          <div style={{
            background: "#fff1f2", border: "1px solid #fecaca",
            borderTop: "4px solid #dc2626", padding: 24, borderRadius: 4,
          }}>
            <div style={{ fontWeight: 800, color: "#7f1d1d", marginBottom: 16, fontSize: 15 }}>
              ❌ Este reporte NO es
            </div>
            {[
              "Asesoría fiscal o representación legal ante el IRS",
              "Una garantía de ahorros específicos — cada situación es diferente",
              "Un sustituto para tu CPA — lo hace más valioso, no innecesario",
              "Basado en estrategias agresivas o de zona gris",
              "Una lista genérica que podrías haber encontrado en Google",
            ].map((t, i) => (
              <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                ✗ {t}
              </p>
            ))}
          </div>
        </div>
      </Sec>

      {/* ══ SECCIÓN 8 — PRECIO (Navy | Teal H2) ══════════════════════════ */}
      <Sec bg={NAVY}>
        <H2Bar barColor={TEAL}>Obtén tu reporte personalizado de preparación con el CPA</H2Bar>

        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <div style={{ color: "#4ed3a6", fontWeight: 700, fontSize: 15, marginBottom: 24 }}>
            ⚡ Precio de lanzamiento: $17 hoy.{" "}
            <span data-testid="es-pricing-spots">{spotsLeft}</span> de 200 lugares disponibles.
          </div>

          <div style={{ marginBottom: 8 }}>
            {pricing.is_launch && (
              <span style={{ color: "#64748b", textDecoration: "line-through", marginRight: 8, fontSize: 22 }}>$27</span>
            )}
            <span style={{ fontSize: 64, fontWeight: 900, color: "#fff" }}>
              {pricing.price_display}
            </span>
            <span style={{ color: "#94a3b8", fontSize: 16, marginLeft: 6 }}>hoy</span>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 28 }}>
            Primeros 200 compradores — sin excepciones después
          </div>

          <div style={{
            background: "#1a3d6b", borderRadius: 4, padding: 24, marginBottom: 28, textAlign: "left",
          }}>
            {[
              "Reporte PDF personalizado de 15–20 páginas — basado en tus respuestas",
              "Puntuación de preparación con el CPA (0–100) con explicación en español",
              "Rango de pago en exceso estimado",
              "Fichas de estrategia para cada brecha identificada",
              "Agenda de reunión con tu CPA — lista para entregar",
              "Plan de acciones rápidas de 7 días",
              "Entrega inmediata a tu correo",
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                <span style={{ color: "#6ee7b7", fontSize: 16 }}>✅</span>
                <span style={{ color: "#e2e8f0", fontSize: 14 }}>{f}</span>
              </div>
            ))}
          </div>

          <div style={{
            background: "#1a3d6b", border: `1px solid ${TEAL}`,
            borderLeft: `4px solid ${TEAL}`, padding: "20px 24px",
            marginBottom: 28, textAlign: "left",
          }}>
            <div style={{ fontWeight: 800, color: "#4ed3a6", fontSize: 15, marginBottom: 10 }}>
              🛡️ Garantía de devolución de 30 días. Sin preguntas.
            </div>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.8, margin: 0 }}>
              Si este reporte no te da claridad sobre tus próximos movimientos fiscales,
              escríbenos en 30 días y te devolvemos cada centavo. Sin formularios. Sin explicación.{" "}
              <strong style={{ color: "#cbd5e1" }}>Puedes incluso quedarte con el reporte. El riesgo es $0.</strong>
            </p>
          </div>

          <Btn onClick={goToQuiz} fullWidth size="lg">
            → Comenzar mi auditoría de 90 segundos — $17
          </Btn>
          <div style={{ marginTop: 14, color: "#64748b", fontSize: 13, lineHeight: 1.8 }}>
            🔒 Pago encriptado · Entrega inmediata · Garantía de 30 días
            <br />
            <span style={{ color: "#475569" }}>Visa · Mastercard · Amex · Discover</span>
          </div>
        </div>
      </Sec>

      {/* ══ SECCIÓN 9 — PREGUNTAS FRECUENTES (White | Navy H2) ═══════════ */}
      <Sec bg="#fff">
        <H2Bar barColor={NAVY}>Cada pregunta que tienes antes de comprar — respondida.</H2Bar>

        {[
          ["¿No puedo encontrar esto gratis en Google?",
           "Puedes encontrar información sobre cada estrategia en este reporte — todo es público. Lo que Google no puede decirte es cuáles de esas estrategias aplican a tus ingresos, tu estado, tu estructura y tu situación específica — y cuánto valen para ti. Eso es lo que estás pagando con $17."],
          ["Ya tengo contador. ¿Este reporte me sirve de algo?",
           "Ese es el mejor caso de uso. Este reporte no reemplaza a tu CPA — transforma la conversación que tienes con él. La mayoría llega y pregunta '¿me estoy perdiendo algo?'. Este reporte te da una agenda específica de qué preguntar, por qué aplica a tu situación y cuál es el impacto en dólares."],
          ["¿Estas estrategias son legítimas?",
           "Cada estrategia en este reporte está basada en la ley fiscal establecida — sin lagunas ni zonas grises. Cada una tiene un número de sección en el código fiscal y forma parte de conversaciones normales de planificación con CPAs."],
          ["¿Qué información necesito proporcionar?",
           "12 preguntas sobre tu nivel de ingresos, estructura de negocio, estrategias actuales y relación con tu CPA. Sin datos personales sensibles. Sin número de Seguro Social. Sin acceso a cuentas bancarias."],
          ["¿Cuándo recibo mi reporte?",
           "En minutos después de completar el pago. Se genera a partir de tus respuestas y se entrega como PDF al correo que ingreses. La mayoría lo recibe en menos de 5 minutos."],
          ["¿Qué pasa si no quedo satisfecho?",
           "Escríbenos a hello@solelytax.ai dentro de 30 días. Te reembolsamos cada centavo. Sin formularios. Sin explicación. Puedes quedarte con el reporte de todos modos."],
        ].map(([q, a], i) => <FaqItem key={i} q={q} a={a} />)}
      </Sec>

      {/* ══ SECCIÓN 10 — CTA FINAL (Navy | Teal H2) ══════════════════════ */}
      <Sec bg={NAVY}>
        <H2Bar barColor={TEAL}>Lo único que cuesta más que $17 es otro año sin un plan.</H2Bar>

        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: "#94a3b8", marginBottom: 12, fontSize: 16, lineHeight: 1.85 }}>
            Cada mes que esperas es dinero que no puedes recuperar.
            Solo puedes dejar de pagar de más en los impuestos del <em>próximo</em> año.
          </p>

          <div style={{ color: "#4ed3a6", fontWeight: 700, fontSize: 15, marginBottom: 28 }}>
            ⚡ <span data-testid="es-final-spots">{spotsLeft}</span> de 200 lugares disponibles a $17.
            Después de eso: $27.
          </div>

          <Btn onClick={goToQuiz} fullWidth size="lg">
            → Comenzar mi auditoría gratuita — Obtener mi reporte por $17
          </Btn>
          <div style={{ marginTop: 14, color: "#64748b", fontSize: 13 }}>
            🔒 Pago seguro · Garantía de devolución de 30 días · Entrega inmediata
          </div>

          <div style={{
            marginTop: 40, background: "#1a3d6b", padding: "20px 24px",
            textAlign: "left", borderLeft: `4px solid ${TEAL}`,
          }}>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.85, margin: 0 }}>
              <strong style={{ color: "#e2e8f0" }}>P.D.</strong> — Respondes 12 preguntas en 90 segundos,
              recibes un reporte personalizado de 15–20 páginas que muestra qué estrategias fiscales
              aplican a tus ingresos y cuánto valen, más una agenda de reunión de una página que puedes
              usar de inmediato. Cuesta $17. Tiene garantía de devolución total de 30 días.
              Lo único sin garantía es el costo de otro año sin claridad.
            </p>
          </div>
        </div>
      </Sec>

      {/* ── PIE DE PÁGINA ────────────────────────────────────────────────── */}
      <footer style={{ background: "#081d36", padding: "36px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <div style={{
            marginBottom: 14, display: "flex", justifyContent: "center",
            gap: 24, flexWrap: "wrap",
          }}>
            {[
              ["Política de Privacidad", "/privacy"],
              ["Términos y Condiciones", "/terms"],
              ["Contacto", "mailto:hello@solelytax.ai"],
              ["English", "/audit"],
            ].map(([label, href]) => (
              <a key={label} href={href}
                style={{ color: "#64748b", fontSize: 13, textDecoration: "none" }}>
                {label}
              </a>
            ))}
          </div>
          <p style={{
            color: "#475569", fontSize: 12, lineHeight: 1.85,
            maxWidth: 600, margin: "0 auto 10px",
          }}>
            Reporte educativo únicamente. No es preparación de impuestos, asesoría legal ni representación
            ante el IRS. Se recomienda usar como base para una conversación con un profesional fiscal
            certificado. Los resultados individuales varían.
          </p>
          <p style={{ color: "#334155", fontSize: 11 }}>
            © 2026 SolelyTax AI — Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
}
